import * as React from 'react';

const MenuBar = ({
  onNewDocument,
  onOpenTemplate,
  onExport,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  isMobile,
  onFixObjectsOutOfBounds,
  onCardConfiguration,
  onMeasurements,
  onAddText,
  onAddArtisticText,
  onAddImage,
  onCameraCapture,
  onDrawSignature,
  onUploadSignature,
  onFingerprintCapture,
  onAddRectangle,
  onAddCircle,
  onAddLine,
  onAddStar,
  onAddQrCode,
  onAddBarcode1D,
  onAddBarcode2D,
  onAddCounter,
  onAddDate,
  onToggleGrid,
  onToggleRuler,
  onToggleSmartGuides,
  onAddNanoText,
  onAddMicroText,
  onAddUVLayer,
  onAddGhostImage,
  onAddHologram,
  onToggleSecurityLayers,
  onExportPlates,
  onZoomIn,
  onZoomOut,
  onFitToWindow,
  onFullscreen,
  onPreview,
  onExportAdvanced,
  onDatabaseOpen,
  onSave
}) => {
  const dispatchDesignerEvent = (eventName) => {
    try { window.dispatchEvent(new CustomEvent(eventName)); } catch { }
  };

  React.useEffect(() => {
    const isTyping = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      const editable = el.isContentEditable;
      return editable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    };

    const onKeyDown = (e) => {
      const key = e.key;
      const lower = key.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      if (isTyping(e.target)) return;

      if (ctrl && lower === 'n') { e.preventDefault(); if (onNewDocument) onNewDocument(); return; }
      if (ctrl && lower === 't') { e.preventDefault(); if (onOpenTemplate) onOpenTemplate(); return; }
      if (ctrl && lower === 'e') { e.preventDefault(); if (onExport) onExport(); return; }
      if (ctrl && lower === 'p') { e.preventDefault(); dispatchDesignerEvent('designer:print'); return; }
      if (ctrl && !e.shiftKey && lower === 's') { e.preventDefault(); if (onSave) onSave(); else dispatchDesignerEvent('designer:save-template-request'); return; }
      if (ctrl && e.shiftKey && lower === 's') { e.preventDefault(); dispatchDesignerEvent('designer:save-master-template'); return; }
      if (ctrl && lower === 'z') { e.preventDefault(); if (onUndo) onUndo(); return; }
      if (ctrl && lower === 'y') { e.preventDefault(); if (onRedo) onRedo(); return; }
      if (ctrl && (key === '=' || key === '+')) { e.preventDefault(); if (onZoomIn) onZoomIn(); return; }
      if (ctrl && (key === '-' || key === '_')) { e.preventDefault(); if (onZoomOut) onZoomOut(); return; }
      if (ctrl && key === '0') { e.preventDefault(); if (onFitToWindow) onFitToWindow(); return; }
      if (key === 'F11') { e.preventDefault(); if (onFullscreen) onFullscreen(); return; }
      if (ctrl && lower === 'g') { e.preventDefault(); if (onToggleGrid) onToggleGrid(); return; }
      if (ctrl && lower === 'r') { e.preventDefault(); if (onToggleRuler) onToggleRuler(); return; }
      if (lower === 't') { if (onAddText) onAddText(); return; }
      if (lower === 'r') { if (onAddRectangle) onAddRectangle(); return; }
      if (lower === 'c') { if (onAddCircle) onAddCircle(); return; }
      if (lower === 'l') { if (onAddLine) onAddLine(); return; }
      if (lower === 'q') { if (onAddQrCode) onAddQrCode(); return; }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onNewDocument, onOpenTemplate, onExport, onUndo, onRedo, onZoomIn, onZoomOut, onFitToWindow, onFullscreen, onToggleGrid, onToggleRuler, onAddText, onAddRectangle, onAddCircle, onAddLine, onAddQrCode, onSave]);

  if (isMobile) {
    return (
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex justify-between items-center">
          <div className="flex space-x-2">
            <button onClick={onNewDocument} className="p-2 text-gray-600 hover:bg-gray-100 rounded" title="Nuevo documento">🆕</button>
            <button onClick={onOpenTemplate} className="p-2 text-gray-600 hover:bg-gray-100 rounded" title="Plantillas">📋</button>
            <button onClick={onUndo} disabled={!canUndo} className="p-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50" title="Deshacer">↶</button>
            <button onClick={onRedo} disabled={!canRedo} className="p-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50" title="Rehacer">↷</button>
            <button onClick={onFixObjectsOutOfBounds} className="p-2 text-red-600 hover:bg-red-100 rounded" title="Corregir objetos">🛠️</button>
          </div>
          <button onClick={onExport} className="bg-blue-600 text-white px-3 py-1 rounded text-sm">Exportar</button>
        </div>
      </div>
    );
  }

  const SaveIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  );

  const OpenIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z" />
    </svg>
  );

  const DatabaseIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  );

  const handleSave = () => {
    if (onSave) onSave();
    else dispatchDesignerEvent('designer:save-template-request');
  };

  const handleOpen = () => {
    if (onOpenTemplate) onOpenTemplate();
  };

  const handleDatabase = () => {
    console.log('🗄️ Database clicked');
    if (onDatabaseOpen) onDatabaseOpen();
  };

  return (
    <div className="flex items-center justify-center gap-3 px-4 py-1">
      <button
        onClick={handleSave}
        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 active:scale-95 font-medium"
        title="Guardar diseño (Ctrl+S)"
      >
        <SaveIcon />
        <span>Guardar</span>
      </button>

      <button
        onClick={handleOpen}
        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 active:scale-95 font-medium"
        title="Abrir plantilla (Ctrl+T)"
      >
        <OpenIcon />
        <span>Abrir</span>
      </button>

      <button
        onClick={handleDatabase}
        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 active:scale-95 font-medium"
        title="Base de datos"
      >
        <DatabaseIcon />
        <span>Database</span>
      </button>
    </div>
  );
};

export default MenuBar;